import React, { useCallback } from 'react'
import { ChainId } from '@nominex/nomiswap-sdk'
import { SlButton } from '@shoelace-style/shoelace/dist/react'
import { ArrowUpIcon, Link, Modal, InjectedModalProps, RemoveIcon, OpenNewIcon } from '@nominex/dex-uikit'
import { TxType } from 'restApi'
import { useTranslation } from 'contexts/Localization'
import { useWagmi } from 'libs/wagmi'
import { getBlockExplorerLink, getBlockExplorerName } from 'utils'
import { IconWrapper } from 'components/styles'
import { AutoColumn } from '../Layout'
import { ConfirmedIcon, StyledBlobLeft, StyledBlobRight, StyledGooeyLoader, Wrapper } from './styles'

export function ConfirmationPendingContent({ pendingText }: { pendingText: string }) {
  const { t } = useTranslation()
  return (
    <Wrapper>
      <ConfirmedIcon>
        <div className="flex w-[150px] h-[150px] overflow-hidden rounded-[50%]">
          <StyledGooeyLoader>
            <StyledBlobLeft />
            <StyledBlobRight />
          </StyledGooeyLoader>
        </div>
      </ConfirmedIcon>
      <AutoColumn gap="12px" justify="center">
        <div className="text-gray-200 text-20">{t('Waiting For Confirmation')}</div>
        <AutoColumn gap="12px" justify="center">
          <div className="font-semibold text-14 text-gray-200 text-center">{pendingText}</div>
        </AutoColumn>
        <div className="text-14 text-dark-gray-100 text-center">{t('Confirm this transaction in your wallet')}</div>
      </AutoColumn>
    </Wrapper>
  )
}

function TransactionSubmittedContent({
  onDismiss,
  chainId,
  hash,
  type,
}: {
  onDismiss: () => void
  hash: string | undefined
  chainId: ChainId
  type: TxType
}) {
  const { t } = useTranslation()

  return (
    <Wrapper>
      <AutoColumn justify="center" gap="18px">
        {type === TxType.REMOVE_LIQUIDITY ? (
          <IconWrapper bgColor="burningOrange500" size="64px">
            <RemoveIcon color="burningOrange500" />
          </IconWrapper>
        ) : (
          <IconWrapper bgColor="primary" size="64px">
            <ArrowUpIcon color="primary" />
          </IconWrapper>
        )}
        <div className="flex flex-col items-center gap-8">
          <div className="font-medium text-gray-200">
            {type === TxType.REMOVE_LIQUIDITY ? t('Liquidity removed') : t('Transaction submitted')}
          </div>
          {chainId && hash && (
            <div className="flex items-center gap-4">
              <Link fontWeight={400} color="primary" external href={getBlockExplorerLink(hash, 'transaction', chainId)}>
                {t('View on %site%', {
                  site: getBlockExplorerName(chainId),
                })}
              </Link>
              <OpenNewIcon color="primary" width="16px" height="16px" />
            </div>
          )}
        </div>
        <SlButton variant="primary" onClick={onDismiss}>
          {t('Close')}
        </SlButton>
      </AutoColumn>
    </Wrapper>
  )
}

export function ConfirmationModalContent({
  bottomContent,
  topContent,
}: {
  topContent: () => React.ReactNode
  bottomContent: () => React.ReactNode
}) {
  return (
    <Wrapper>
      <div>{topContent()}</div>
      <div>{bottomContent()}</div>
    </Wrapper>
  )
}

interface ConfirmationModalProps {
  title: string
  customOnDismiss?: () => void
  hash: string | undefined
  content: () => React.ReactNode
  attemptingTxn: boolean
  pendingText: string
  txType: TxType
}

const TransactionConfirmationModal: React.FC<InjectedModalProps & ConfirmationModalProps> = ({
  title,
  onDismiss,
  customOnDismiss,
  attemptingTxn,
  hash,
  pendingText,
  content,
  txType,
}) => {
  const { chainId } = useWagmi()

  const handleDismiss = useCallback(() => {
    if (customOnDismiss) {
      customOnDismiss()
    }
    onDismiss()
  }, [customOnDismiss, onDismiss])

  if (!chainId) return null

  return (
    <Modal title={title} onDismiss={handleDismiss}>
      {attemptingTxn ? (
        <ConfirmationPendingContent pendingText={pendingText} />
      ) : hash ? (
        <TransactionSubmittedContent chainId={chainId} hash={hash} onDismiss={handleDismiss} type={txType} />
      ) : (
        content()
      )}
    </Modal>
  )
}

export default TransactionConfirmationModal
